<script setup lang="ts">
import { RouterLink, RouterView } from 'vue-router'
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAuthStore } from './stores/authStore'
import { useLocalizedRouter } from './composables/useLocalizedRouter'
import CookieBanner from './components/CookieBanner.vue'
import ContactFloat from './components/ContactFloat.vue'
import AuthManager from './components/AuthManager.vue'
import AuthNotification from './components/AuthNotification.vue'
import MobileNav from './components/MobileNav.vue'
import BookingModal from './components/BookingModal.vue'
import { useAppointmentStore } from './stores/appointmentStore'
// Interface pour le devis
interface Quote {
  id: string;
  fileName: string;
  parameters: {
    scale: number;
    material: string;
    technology: string;
    quality: string;
    infill: number;
    color: string;
    quantity: number;
  };
  price: number;
  createdAt: Date;
}

const isMenuOpen = ref(false)
const isScrolled = ref(false)
const isAccountMenuOpen = ref(false)
const headerRef = ref<HTMLElement | null>(null)
let headerResizeObserver: ResizeObserver | null = null

const syncHeaderHeight = (heightPx?: number) => {
  if (!heightPx) return
  document.documentElement.style.setProperty('--header-height', `${heightPx}px`)
}
const { t } = useI18n({
  inheritLocale: true,
  useScope: 'global'
})

// Utiliser le store d'authentification
const authStore = useAuthStore()

// Store pour la réservation d'appel
const appointmentStore = useAppointmentStore()

// Utiliser le router localisé
const { localizedPath } = useLocalizedRouter()


// Ajout de la logique du panier avec le bon typage
const cart = ref<Quote[]>([])
const cartCount = computed(() => cart.value.length)

// Fonction pour ajouter un devis au panier avec le bon typage
const addToCart = (quote: Quote) => {
  cart.value.push(quote)
  // Émettre l'événement de mise à jour immédiatement
  window.dispatchEvent(new CustomEvent('cart-updated', { detail: cart.value }))
}

let scrollRaf = 0
const handleScroll = () => {
  if (scrollRaf) return
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = 0
    isScrolled.value = window.scrollY > 20
  })
}

// Handler pour la mise à jour du panier
const handleAddToCart = (event: CustomEvent<Quote>) => {
  addToCart(event.detail)
}

// Handler pour la suppression du panier
const handleRemoveFromCart = (event: CustomEvent<string>) => {
  const idToRemove = event.detail
  cart.value = cart.value.filter(item => item.id !== idToRemove)
  // Émettre l'événement de mise à jour immédiatement
  window.dispatchEvent(new CustomEvent('cart-updated', { detail: cart.value }))
}

// Handler pour la demande d'état
const handleRequestCartState = () => {
  window.dispatchEvent(new CustomEvent('cart-updated', { detail: cart.value }))
}

// Handler pour la connexion utilisateur
const handleUserLogin = () => {
  // Le store d'authentification gère automatiquement l'état
  // Pas besoin de mettre à jour manuellement les variables
}

// Computed properties pour l'authentification
const isAuthenticated = computed(() => authStore.isAuthenticated)

const router = useRouter()

// Fonction pour fermer tous les menus
const closeAllMenus = () => {
  isMenuOpen.value = false
  isAccountMenuOpen.value = false
}

// Bloquer le scroll quand le menu mobile est ouvert
watch(isMenuOpen, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

// Fonction pour gérer les clics en dehors des menus
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  // Ne pas fermer si on clique sur le menu-toggle ou dans le nav
  if (target.closest('.menu-toggle') || target.closest('nav')) {
    return
  }
  if (!target.closest('.account-menu')) {
    closeAllMenus()
  }
}

// Fonction pour ouvrir un menu spécifique
const openMenu = (menuType: 'account') => {
  closeAllMenus()
  if (menuType === 'account') {
    isAccountMenuOpen.value = true
  }
}

// Fonction de déconnexion
const logout = async () => {
  await authStore.logout()
  closeAllMenus()
  // Rediriger vers la homepage en remplaçant l'historique avec le chemin localisé
  router.replace(localizedPath('/')).catch(() => {
    // Si la redirection échoue, forcer le rechargement vers la homepage
    window.location.href = localizedPath('/')
  })
}

onMounted(() => {
  // Vérifier l'authentification et le rôle admin via le store
  if (authStore.token && !authStore.user) {
    authStore.fetchCurrentUser()
  }

  if (headerRef.value && typeof ResizeObserver !== 'undefined') {
    headerResizeObserver = new ResizeObserver((entries) => {
      const box = entries[0]?.borderBoxSize?.[0]
      const height = box?.blockSize ?? entries[0]?.contentRect?.height
      if (height) syncHeaderHeight(height)
    })
    headerResizeObserver.observe(headerRef.value)
  } else if (headerRef.value?.offsetHeight) {
    syncHeaderHeight(headerRef.value.offsetHeight)
  }

  window.addEventListener('scroll', handleScroll, { passive: true })
  window.addEventListener('add-to-cart', handleAddToCart as EventListener)
  window.addEventListener('remove-from-cart', handleRemoveFromCart as EventListener)
  window.addEventListener('request-cart-state', handleRequestCartState as EventListener)
  window.addEventListener('user-login', handleUserLogin as EventListener)
  document.addEventListener('click', handleClickOutside)

  // Initialiser le panier depuis le localStorage si disponible
  const savedCart = localStorage.getItem('cart')
  if (savedCart) {
    try {
      const parsedCart = JSON.parse(savedCart)
      // Convertir les dates string en objets Date
      cart.value = parsedCart.map((item: any) => ({
        ...item,
        createdAt: new Date(item.createdAt)
      }))
      // Émettre l'événement de mise à jour immédiatement
      window.dispatchEvent(new CustomEvent('cart-updated', { detail: cart.value }))
    } catch (e) {
      console.error('Erreur lors de la lecture du panier:', e)
    }
  }
})

onUnmounted(() => {
  headerResizeObserver?.disconnect()
  headerResizeObserver = null
  // Restaurer le scroll au cas où le menu serait encore ouvert
  document.body.style.overflow = ''
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('add-to-cart', handleAddToCart as EventListener)
  window.removeEventListener('remove-from-cart', handleRemoveFromCart as EventListener)
  window.removeEventListener('request-cart-state', handleRequestCartState as EventListener)
  window.removeEventListener('user-login', handleUserLogin as EventListener)
  document.removeEventListener('click', handleClickOutside)
})

// Sauvegarder le panier dans le localStorage à chaque modification
watch(cart, (newCart) => {
  localStorage.setItem('cart', JSON.stringify(newCart))
  window.dispatchEvent(new CustomEvent('cart-updated', { detail: newCart }))
}, { deep: true })
</script>

<template>
  <!-- Composant de gestion de l'authentification -->
  <AuthManager />
  
  <!-- Composant de notification d'authentification -->
  <AuthNotification ref="authNotification" />

  <!-- Modal de réservation d'appel téléphonique -->
  <BookingModal />

  <header ref="headerRef" class="header" :class="{ 'scrolled': isScrolled }">
    <nav class="desktop-nav" aria-label="Navigation principale">
      <div class="nav-left">
        <RouterLink :to="localizedPath('/')" class="logo" :aria-label="t('nav.home') + ' - PostDev'">
          <div class="logo-icon">
            <i class="fas fa-code" aria-hidden="true"></i>
          </div>
          <span class="logo-text">PostDev</span>
        </RouterLink>
      </div>
      <div class="nav-center">
        <RouterLink :to="localizedPath('/')">{{ t('nav.home') }}</RouterLink>
        <RouterLink :to="localizedPath('/services')">{{ t('nav.services') }}</RouterLink>
        <RouterLink :to="localizedPath('/portfolio')">{{ t('nav.portfolio') }}</RouterLink>
        <RouterLink :to="localizedPath('/blog')">{{ t('nav.blog') }}</RouterLink>
        <RouterLink :to="localizedPath('/outils')">{{ t('nav.tools') }}</RouterLink>
        <RouterLink :to="localizedPath('/contact')">{{ t('nav.contact') }}</RouterLink>
      </div>
      <div class="nav-right">
        <button class="btn btn-primary btn-book-call" @click="appointmentStore.openModal()">
          <i class="fas fa-phone" aria-hidden="true"></i>
          {{ t('nav.bookCall') }}
        </button>

        <div class="account-menu">
          <button class="btn btn-primary account-btn" @click="openMenu('account')" :aria-expanded="isAccountMenuOpen" :aria-label="t('nav.account')">
            <i class="fas fa-user" aria-hidden="true"></i>
            {{ t('nav.account') }}
          </button>
          <div class="account-dropdown" v-show="isAccountMenuOpen" role="menu">
            <template v-if="isAuthenticated">
              <RouterLink :to="localizedPath('/account')" class="dropdown-item" role="menuitem" @click="isAccountMenuOpen = false">
                <i class="fas fa-user-circle" aria-hidden="true"></i>
                {{ t('nav.account') }}
              </RouterLink>
              <RouterLink :to="localizedPath('/dashboard')" class="dropdown-item" role="menuitem" @click="isAccountMenuOpen = false">
                <i class="fas fa-tachometer-alt" aria-hidden="true"></i>
                {{ t('nav.dashboard') }}
              </RouterLink>
              <RouterLink :to="localizedPath('/my-projects')" class="dropdown-item" role="menuitem" @click="isAccountMenuOpen = false">
                <i class="fas fa-project-diagram" aria-hidden="true"></i>
                {{ t('nav.myProjects') }}
              </RouterLink>
              <button class="dropdown-item" role="menuitem" @click="logout(), isAccountMenuOpen = false" :aria-label="t('nav.logout')">
                <i class="fas fa-sign-out-alt" aria-hidden="true"></i>
                {{ t('nav.logout') }}
              </button>
            </template>
            <template v-else>
              <RouterLink :to="localizedPath('/auth/login')" class="dropdown-item" role="menuitem" @click="isAccountMenuOpen = false">
                <i class="fas fa-sign-in-alt" aria-hidden="true"></i>
                {{ t('nav.login') }}
              </RouterLink>
              <RouterLink :to="localizedPath('/auth/signup')" class="dropdown-item" role="menuitem" @click="isAccountMenuOpen = false">
                <i class="fas fa-user-plus" aria-hidden="true"></i>
                {{ t('nav.register') }}
              </RouterLink>
            </template>
          </div>
        </div>
      </div>
    </nav>

    <!-- Logo mobile (visible uniquement en dessous de 1280px) -->
    <RouterLink :to="localizedPath('/')" class="mobile-header-logo" :aria-label="t('nav.home') + ' - PostDev'">
      <div class="logo-icon">
        <i class="fas fa-code" aria-hidden="true"></i>
      </div>
      <span class="logo-text">PostDev</span>
    </RouterLink>

    <!-- Menu Mobile -->
    <button class="menu-toggle" :class="{ 'active': isMenuOpen }" @click="isMenuOpen = !isMenuOpen" :aria-label="isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'">
      <span class="menu-icon"></span>
    </button>

    <MobileNav :isOpen="isMenuOpen" @close="isMenuOpen = false" />
  </header>

  <main id="main-content" tabindex="-1">
    <div class="main-content">
      <RouterView />
    </div>
  </main>

  <CookieBanner />

  <ContactFloat />

  <footer class="footer">
    <div class="footer-content">
      <div class="footer-section">
        <h3>{{ t('footer.about.title') }}</h3>
        <p>{{ t('footer.about.description') }}</p>
      </div>

      <div class="footer-section">
        <h3>{{ t('footer.nav.title') }}</h3>
        <ul class="footer-nav-links">
          <li>
            <RouterLink :to="localizedPath('/')">{{ t('nav.home') }}</RouterLink>
          </li>
          <li>
            <RouterLink :to="localizedPath('/services')">{{ t('nav.services') }}</RouterLink>
          </li>
          <li>
            <RouterLink :to="localizedPath('/about')">{{ t('nav.about') }}</RouterLink>
          </li>
          <li>
            <RouterLink :to="localizedPath('/portfolio')">{{ t('nav.portfolio') }}</RouterLink>
          </li>
          <li>
            <RouterLink :to="localizedPath('/blog')">{{ t('nav.blog') }}</RouterLink>
          </li>
          <li>
            <RouterLink :to="localizedPath('/local')">{{ t('footer.nav.zones') }}</RouterLink>
          </li>
          <li>
            <RouterLink :to="localizedPath('/outils')">{{ t('footer.nav.tools') }}</RouterLink>
          </li>
          <li>
            <RouterLink :to="localizedPath('/contact')">{{ t('nav.contact') }}</RouterLink>
          </li>
        </ul>
      </div>

      <div class="footer-section">
        <h3>{{ t('footer.contact.title') }}</h3>
        <ul class="footer-contact-links">
          <li>
            <i class="fas fa-envelope" aria-hidden="true"></i>
            <a href="mailto:contact@postdev.fr">contact@postdev.fr</a>
          </li>
          <li>
            <i class="fas fa-phone" aria-hidden="true"></i>
            <a href="tel:+33616868688" type="tel">+33 6 16 86 86 88</a>
          </li>
          <li>
            <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
            <span>Lillers, France</span>
          </li>
        </ul>
      </div>

      <div class="footer-section">
        <h3>{{ t('footer.social.title') }}</h3>
        <div class="social-links">
          <a href="https://www.linkedin.com/company/postdev" target="_blank" rel="noopener noreferrer" :aria-label="t('footer.social.linkedin')">
            <i class="fab fa-linkedin" aria-hidden="true"></i>
            <span>{{ t('footer.social.linkedin') }}</span>
          </a>
          <a href="https://www.facebook.com/profile.php?id=61579827748148" target="_blank" rel="noopener noreferrer" :aria-label="t('footer.social.facebook')">
            <i class="fab fa-facebook" aria-hidden="true"></i>
            <span>{{ t('footer.social.facebook') }}</span>
          </a>
          <a href="https://www.instagram.com/postdev.fr/" target="_blank" rel="noopener noreferrer" :aria-label="t('footer.social.instagram')">
            <i class="fab fa-instagram" aria-hidden="true"></i>
            <span>{{ t('footer.social.instagram') }}</span>
          </a>
        </div>
      </div>
    </div>

    <div class="footer-bottom">
      <div class="legal-links">
        <RouterLink :to="localizedPath('/legal/notice')">{{ t('footer.legal.notice') }}</RouterLink>
        <RouterLink :to="localizedPath('/legal/terms')">{{ t('footer.legal.terms') }}</RouterLink>
        <RouterLink :to="localizedPath('/legal/privacy')">{{ t('footer.legal.privacy') }}</RouterLink>
        <RouterLink :to="localizedPath('/plan-du-site')">{{ t('footer.legal.sitemap') }}</RouterLink>
      </div>
      <p class="copyright">&copy; {{ new Date().getFullYear() }} PostDev. {{ t('footer.rights') }}</p>
    </div>
  </footer>
</template>

<style>

:root {
  --primary-color: rgb(200, 30, 30);
  --primary-dark: rgb(170, 24, 24);
  --primary-light: rgb(248, 113, 113);
  --primary-color-rgb: 200, 30, 30;
  --text-primary: #1a1a1a;
  --text-secondary: #525252;
  /* Texte secondaire sur fond footer sombre (WCAG AA, ~7:1 sur #1a1a1a) */
  --footer-text-muted: rgba(255, 255, 255, 0.92);
  --background-light: #f8fafc;
  --header-height: 4rem;
  --border-radius: 8px;
  --shadow-sm: 0 2px 4px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 10px 15px rgba(0, 0, 0, 0.1);
  --transition: all 0.3s ease;
  --font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  scrollbar-gutter: stable;
  overflow-x: clip;
}

body {
  font-family: var(--font-family);
  color: var(--text-primary);
  line-height: 1.6;
  overflow-x: clip;
  font-weight: 400;
}

/* Réserve la boîte des icônes tant que le CSS Font Awesome (différé) n’est pas appliqué. */
.fas,
.far,
.fab,
.fal,
.fad,
.fa,
.fa-solid,
.fa-regular,
.fa-brands {
  display: inline-block;
  width: 1.25em;
  height: 1em;
  line-height: 1;
  font-style: normal;
  font-variant: normal;
  text-rendering: auto;
  flex-shrink: 0;
  vertical-align: -0.125em;
}

h1, h2, h3, h4, h5, h6 {
  font-family: var(--font-family);
  font-weight: 700;
  line-height: 1.2;
}

img {
  max-width: 100%;
  height: auto;
}

a {
  color: inherit;
  text-decoration: none;
}

button {
  cursor: pointer;
  border: none;
  background: none;
  font: inherit;
}

.container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.section {
  padding: 6rem 0;
}

.section-title {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  text-align: center;
  font-family: var(--font-family);
  font-weight: 700;
}

.section-subtitle {
  font-size: 1.1rem;
  color: var(--text-secondary);
  text-align: center;
  max-width: 600px;
  margin: 0 auto 3rem;
  font-family: var(--font-family);
  font-weight: 400;
}

.grid {
  display: grid;
  gap: 2rem;
}

.flex {
  display: flex;
  gap: 1rem;
}

.card {
  background: white;
  border-radius: var(--border-radius);
  padding: 2rem;
  box-shadow: var(--shadow-md);
  transition: var(--transition);
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-lg);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius);
  font-weight: 500;
  transition: var(--transition);
}

.btn-primary {
  background: var(--primary-color);
  color: white;
}

.btn-primary:hover {
  background: var(--primary-dark);
  transform: translateY(-2px);
}

.btn-secondary {
  background: white;
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
}

.btn-secondary:hover {
  background: var(--primary-color);
  color: white;
}

@media (max-width: 768px) {
  .section {
    padding: 4rem 0;
  }

  .section-title {
    font-size: 2rem;
  }

  .grid {
    grid-template-columns: 1fr;
  }

  .flex {
    flex-direction: column;
  }
}
</style>

<style scoped>
.header {
  padding: 0.75rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  position: sticky;
  width: 100%;
  top: 0;
  z-index: 100;
  transition: box-shadow 0.3s ease;
}

.header.scrolled {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.logo {
  text-decoration: none;
  font-size: 1.75rem;
  font-weight: 800;
  color: var(--primary-color);
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: transform 0.3s ease;
  font-family: var(--font-family);
}

.logo:hover {
  transform: scale(1.05);
}

.logo-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background: var(--primary-color);
  border-radius: 50%;
  color: white;
  font-size: 1.25rem;
  transition: all 0.3s ease;
}

.logo:hover .logo-icon {
  transform: rotate(180deg);
  background: var(--primary-dark);
}

.logo-text {
  font-size: 1.5rem;
  font-family: var(--font-family);
  background: linear-gradient(45deg, var(--primary-color), var(--primary-dark));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -0.5px;
}

.mobile-header-logo {
  display: none;
  text-decoration: none;
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--primary-color);
  align-items: center;
  gap: 0.75rem;
  transition: transform 0.3s ease;
  font-family: var(--font-family);
}

.mobile-header-logo:hover {
  transform: scale(1.05);
}

.desktop-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: relative;
}

.desktop-nav .nav-left {
  display: flex;
  align-items: center;
  z-index: 2;
}

.desktop-nav .nav-center {
  display: flex;
  gap: 1.5rem;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
}

.desktop-nav .nav-right {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-left: 2rem;
}

.desktop-nav .nav-center a {
  text-decoration: none;
  color: var(--text-primary);
  font-weight: 500;
  font-size: 1.05rem;
  transition: all 0.3s ease;
  position: relative;
  padding: 0.5rem 0;
  font-family: var(--font-family);
}

.desktop-nav .nav-center a:hover,
.desktop-nav .nav-center a.router-link-active {
  color: var(--primary-color);
}

.desktop-nav .nav-center a::after {
  content: '';
  position: absolute;
  width: 0;
  height: 2px;
  bottom: 0;
  left: 50%;
  background-color: var(--primary-color);
  transition: all 0.3s ease;
  transform: translateX(-50%);
}

.desktop-nav .nav-center a:hover::after,
.desktop-nav .nav-center a.router-link-active::after {
  width: 100%;
}

/* Exclure le bouton contact du soulignement actif */
.desktop-nav a.contact-btn.router-link-active::after {
  display: none;
}

.contact-btn {
  background: var(--primary-color);
  color: white !important;
  padding: 0.5rem 1.25rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.2);
  font-family: var(--font-family);
}

.contact-btn:hover {
  background: var(--primary-dark);
  box-shadow: 0 6px 12px rgba(37, 99, 235, 0.3);
}

.contact-btn::after {
  display: none;
}

/* Menu Mobile Toggle */
.menu-toggle {
  display: none;
}

@media (max-width: 1279px) {
  .desktop-nav {
    display: none !important;
  }

  .mobile-header-logo {
    display: flex;
  }

  .menu-toggle {
    display: block;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.75rem;
    position: relative;
    width: 44px;
    height: 44px;
    z-index: 1001;
    border-radius: 8px;
    transition: background-color 0.2s ease;
  }

  .header {
    padding: 0.5rem 1rem;
  }

  .menu-toggle.active {
    z-index: 1002;
    display: none;
  }

  .menu-toggle:hover {
    background: rgba(var(--primary-color-rgb), 0.1);
  }

  .menu-toggle:active {
    background: rgba(var(--primary-color-rgb), 0.2);
  }

  .menu-icon,
  .menu-icon::before,
  .menu-icon::after {
    content: '';
    display: block;
    width: 24px;
    height: 2px;
    border-radius: 3px;
    background: var(--primary-color);
    transition: all 0.4s cubic-bezier(0.68, -0.6, 0.32, 1.6);
    position: absolute;
  }

  .menu-icon {
    top: 50%;
    transform: translateY(-50%);
  }

  .menu-icon::before {
    content: '';
    top: -8px;
    transform-origin: right;
    width: 18px;
    right: 0;
  }

  .menu-icon::after {
    content: '';
    bottom: -8px;
    transform-origin: right;
    width: 22px;
    right: 0;
  }

  .menu-toggle.active .menu-icon {
    transform: rotate(45deg);
    width: 24px;
  }

  .menu-toggle.active .menu-icon::before {
    width: 24px;
    top: 0;
    transform: rotate(90deg);
  }

  .menu-toggle.active .menu-icon::after {
    width: 24px;
    bottom: 0;
    transform: rotate(90deg);
  }
}

/* Menu Desktop - visible uniquement sur desktop */
@media (min-width: 1280px) {
  .desktop-nav {
    display: flex;
  }
}

.footer {
  background: var(--text-primary);
  color: white;
  padding: 3rem 1.5rem 2rem;
  margin-top: auto;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 0.9fr);
  gap: 1.25rem 2rem;
  margin-bottom: 2.5rem;
  align-items: start;
}

.footer-section {
  padding: 0;
  min-width: 0;
}

.footer-section h3 {
  font-size: 1.05rem;
  margin-bottom: 1rem;
  color: white;
  position: relative;
  display: inline-block;
}

.footer-section h3::after {
  content: '';
  position: absolute;
  bottom: -0.5rem;
  left: 0;
  width: 100%;
  height: 2px;
  background: var(--primary-color);
}

.footer-section p {
  color: var(--footer-text-muted);
  line-height: 1.55;
  font-size: 0.92rem;
  margin: 0;
}

.footer-section ul {
  list-style: none;
  padding: 0;
}

.footer-nav-links {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1.25rem;
  row-gap: 0.35rem;
}

.footer-section .footer-nav-links li {
  margin: 0;
  padding: 0;
  display: block;
  line-height: 1.4;
}

.footer-nav-links a {
  color: var(--footer-text-muted);
  text-decoration: none;
  transition: var(--transition);
  display: block;
  padding: 0.2rem 0;
  line-height: 1.4;
  font-size: 0.92rem;
}

.footer-nav-links a:hover {
  color: white;
}

.footer-contact-links {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.footer-section .footer-contact-links li {
  margin: 0;
  padding: 0.2rem 0;
  color: var(--footer-text-muted);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  line-height: 1.4;
  font-size: 0.92rem;
  transition: var(--transition);
}

.footer-contact-links a {
  color: var(--footer-text-muted);
  text-decoration: none;
  transition: color 0.3s ease;
  padding: 0;
  line-height: 1.3;
}

.footer-contact-links a:hover {
  color: white;
}

.footer-section .footer-contact-links li:hover {
  color: white;
}

.social-links {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
}

.social-links a {
  width: fit-content;
  max-width: 100%;
  height: auto;
  min-height: 2.25rem;
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  color: white;
  font-size: 0.9rem;
  transition: var(--transition);
  text-decoration: none;
}

.social-links a span {
  font-size: 0.85rem;
  font-weight: 500;
}

.social-links a:hover {
  background: var(--primary-color);
  transform: translateY(-3px);
}

.footer-bottom {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem 1rem 0;
}

.legal-links {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
}

.legal-links a {
  color: var(--footer-text-muted);
  text-decoration: none;
  transition: var(--transition);
  position: relative;
  display: inline-flex;
  align-items: center;
  min-height: 48px;
  padding: 0.75rem 0.5rem;
}

.legal-links a:hover {
  color: white;
}

.legal-links a::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 1px;
  background: var(--primary-color);
  transition: var(--transition);
}

.legal-links a:hover::after {
  width: 100%;
}

.copyright {
  color: var(--footer-text-muted);
  font-size: 0.9rem;
}

@media (max-width: 900px) {
  .footer-content {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2rem 1.5rem;
  }
}

@media (max-width: 768px) {
  .footer {
    padding: 3rem 1rem 1.5rem;
  }

  .footer-content {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }

  .footer-section {
    padding: 0;
    text-align: center;
  }

  .footer-section h3 {
    display: inline-block;
    width: auto;
    text-align: center;
  }

  .footer-section h3::after {
    width: 100%;
  }

  .footer-section .footer-contact-links li {
    justify-content: center;
  }

  .footer-nav-links {
    align-items: center;
    grid-template-columns: 1fr;
    justify-items: center;
  }

  .footer-nav-links a {
    padding: 0.15rem 0;
  }

  .social-links {
    align-items: center;
  }

  .social-links a {
    justify-content: center;
    width: auto;
    max-width: 100%;
  }

  .footer-bottom {
    flex-direction: column;
    text-align: center;
    gap: 1.5rem;
  }

  .legal-links {
    justify-content: center;
    gap: 1.5rem;
  }
}

@media (max-width: 480px) {
  .footer {
    padding: 2.5rem 1rem 1rem;
  }

  .footer-content {
    gap: 2rem;
  }

  .legal-links {
    flex-direction: column;
    gap: 1rem;
  }

  .legal-links a {
    padding: 0.5rem 0;
  }
}

main {
  min-height: calc(100vh - var(--header-height));
}

/* Wrapper pour le contenu principal */
.main-content {
  position: relative;
  z-index: 1;
}

/* Suppression des styles du cart-btn */
.cart-btn {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius);
  transition: var(--transition);
}

.cart-btn:hover {
  background: rgba(var(--primary-color-rgb), 0.1);
}

.cart-count {
  position: absolute;
  top: -8px;
  right: -8px;
  background: var(--primary-color);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
}

/* Styles pour le menu compte */
.account-menu {
  position: relative;
}

.account-menu .account-btn,
.nav-right .account-btn {
  background: transparent !important;
  border: 2px solid #6b7280 !important;
  color: #6b7280 !important;
  height: 2.5rem;
  padding: 0 1.15rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1;
  border-radius: var(--border-radius);
  box-shadow: none !important;
  white-space: nowrap;
}

.account-menu .account-btn:hover,
.nav-right .account-btn:hover {
  background: #6b7280 !important;
  color: white !important;
  border-color: #6b7280 !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3) !important;
}

.account-menu .account-btn i,
.nav-right .account-btn i {
  color: inherit;
}

.account-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border-radius: var(--border-radius);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  min-width: 200px;
  z-index: 1000;
  overflow: hidden;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem !important;
  color: var(--text-primary);
  text-decoration: none;
  transition: background-color 0.3s;
  cursor: pointer;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  font: inherit;
}

.dropdown-item:hover {
  background-color: var(--background-light);
  color: var(--primary-color);
}

.dropdown-item i {
  width: 1.25rem;
}

/* S'assurer que les liens RouterLink dans le dropdown ont le bon padding */
.account-dropdown .dropdown-item,
.account-dropdown a.dropdown-item {
  padding: 0.75rem 1rem !important;
}

/* S'assurer que les styles desktop-nav n'affectent pas le dropdown */
.account-dropdown .dropdown-item::after {
  display: none;
}

@media (max-width: 768px) {
  .account-dropdown {
    position: static;
    box-shadow: none;
    margin-top: 0.5rem;
  }
}




.auth-links {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-left: 1rem;
}

.auth-links .btn {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  border-radius: var(--border-radius);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.auth-links .btn-secondary {
  background: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
}

.auth-links .btn-secondary:hover {
  background: var(--primary-color);
  color: white;
}

.auth-links .btn-primary {
  background: var(--primary-color);
  color: white;
}

.auth-links .btn-primary:hover {
  background: var(--primary-dark);
  transform: translateY(-2px);
}

@media (max-width: 1024px) {
  .auth-links {
    flex-direction: column;
    margin: 1rem 0;
    width: 100%;
  }

  .auth-links .btn {
    width: 100%;
    justify-content: center;
  }
}

.desktop-nav .nav-right .btn-primary {
  background: transparent;
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
  height: 2.5rem;
  padding: 0 1.15rem;
  border-radius: 25px;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  transition: all 0.3s ease;
}

.desktop-nav .nav-right .btn-primary::after {
  display: none;
}

.desktop-nav .nav-right .btn-primary:hover {
  background: var(--primary-color);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(var(--primary-color-rgb), 0.2);
}

.desktop-nav .nav-right .btn-primary i {
  margin-right: 0;
}

.desktop-nav .nav-right .btn-book-call i {
  margin-right: 0;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>


